import { detectEnvironment } from '@zektec/environment-utils';

export type FrontendEnvironmentPillTone =
  | 'local'
  | 'stage'
  | 'test'
  | 'development'
  | 'unknown';

export interface FrontendEnvironmentPillInput {
  apiBaseUrl?: string | URL | null | undefined;
  environmentName?: string | null | undefined;
  identityServiceUrl?: string | URL | null | undefined;
  siteUrl?: string | URL | null | undefined;
}

export interface FrontendEnvironmentPillConfig {
  label: string;
  tone: FrontendEnvironmentPillTone;
}

function normalizeValue(value: string | URL | null | undefined): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = value.toString().trim();
  return normalized.length > 0 ? normalized : null;
}

function getHostname(value: string | null): string | null {
  if (!value) {
    return null;
  }

  try {
    return new URL(value).hostname.toLowerCase();
  } catch {
    return null;
  }
}

function normalizeEnvironmentName(
  environmentName: string | null,
): string | null {
  return environmentName?.trim().toLowerCase() ?? null;
}

function titleCaseEnvironmentName(environmentName: string): string {
  return environmentName
    .split(/[-_\s.]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

const TOV_PRODUCTION_HOST_INDICATORS = [
  'tovplus.com',
  'tov.plus',
  'dash.tovplus.com',
  'admin.tovplus.com',
  'vendors.tovplus.com',
  'docs.tovplus.com',
];

const TOV_STAGE_INDICATORS = ['stage', 'staging', 'release-stage-', '.stage.'];

const TOV_LOCAL_HOST_INDICATORS = [
  'localhost',
  '127.0.0.1',
  '0.0.0.0',
  '::1',
  '.tovplus.local',
];

function isProductionHostname(hostname: string | null): boolean {
  return (
    hostname === 'tovplus.com' ||
    hostname === 'tov.plus' ||
    hostname === 'dash.tovplus.com' ||
    hostname === 'admin.tovplus.com' ||
    hostname === 'vendors.tovplus.com' ||
    hostname === 'docs.tovplus.com'
  );
}

function detectFrontendEnvironment(input: {
  environmentName: string | null;
  values: Array<string | null>;
}) {
  const modeEnvironment = detectEnvironment({
    mode: input.environmentName ?? undefined,
    productionHostIndicators: TOV_PRODUCTION_HOST_INDICATORS,
    stagingIndicators: TOV_STAGE_INDICATORS,
    localHostIndicators: TOV_LOCAL_HOST_INDICATORS,
  });

  if (modeEnvironment !== 'unknown') {
    return modeEnvironment;
  }

  for (const origin of input.values) {
    const detected = detectEnvironment({
      origin: origin ?? undefined,
      productionHostIndicators: TOV_PRODUCTION_HOST_INDICATORS,
      stagingIndicators: TOV_STAGE_INDICATORS,
      localHostIndicators: TOV_LOCAL_HOST_INDICATORS,
    });

    if (detected !== 'unknown') {
      return detected;
    }
  }

  return 'unknown';
}

export function resolveFrontendEnvironmentPill(
  input: FrontendEnvironmentPillInput = {},
): FrontendEnvironmentPillConfig | null {
  const environmentName = normalizeEnvironmentName(
    normalizeValue(input.environmentName),
  );
  const values = [
    normalizeValue(input.siteUrl),
    normalizeValue(input.apiBaseUrl),
    normalizeValue(input.identityServiceUrl),
  ];
  const hostnames = values.map(getHostname);
  const detectedEnvironment = detectFrontendEnvironment({
    environmentName,
    values,
  });

  if (environmentName === 'production' || environmentName === 'prod') {
    return null;
  }

  if (
    !environmentName &&
    (detectedEnvironment === 'production' ||
      hostnames.some(isProductionHostname))
  ) {
    return null;
  }

  if (
    detectedEnvironment === 'local' ||
    detectedEnvironment === 'development'
  ) {
    return {
      label: environmentName === 'development' ? 'Development' : 'Local',
      tone: environmentName === 'development' ? 'development' : 'local',
    };
  }

  if (detectedEnvironment === 'test') {
    return {
      label: 'Test',
      tone: 'test',
    };
  }

  if (detectedEnvironment === 'staging') {
    return {
      label: 'Stage',
      tone: 'stage',
    };
  }

  if (environmentName) {
    return {
      label: titleCaseEnvironmentName(environmentName),
      tone: 'unknown',
    };
  }

  return null;
}
